import { useEffect, Fragment, useRef, useState, useMemo } from "react";
import Sidebar from "../../components/Sidebar";
import {
    CalendarIcon,
    CheckIcon,
    ClockIcon,
    PlusIcon,
    Bars3CenterLeftIcon,
    Cog6ToothIcon,
    MagnifyingGlassIcon,
    ChevronDownIcon,
    XMarkIcon,
    AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { DialogMenu } from "./components/DialogMenu";
import api from "../../api";
import { AppointmentForm } from "./components/AppointmentForm";
import { AppointmentTable } from "./components/AppointmentTable";
import { Menu, Transition } from "@headlessui/react";
import SimpleCombobox from "../../components/SimpleCombobox";
import { DrAppointmentTable } from "./components/DrAppointmentTable";
import toast, { Toaster } from "react-hot-toast";
import DayCalendar from "./components/DayCalendar";
import { useSchedule } from "../../context/ScheduleContext";
import {
    useFilteredAppointments,
    useMergedSchedule,
} from "../../hooks/scheduleHooks";

export function SchedulePage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const dialogRef = useRef(null);
    const editDialogRef = useRef(null);
    const {
        appointments,
        doctorSchedule,
        selectedDoctor,
        setSelectedDoctor,
        selectedDate,
        setSelectedDate,
        loadingAppointments,
        loadingSchedule,
        counter,
        fetchAppointments,
        fetchSchedule,
        setSearchTerm,
        searchAllDates,
        setSearchAllDates,
        setCounter,
    } = useSchedule();
    const mergedSchedule = useMergedSchedule(
        appointments,
        doctorSchedule,
        selectedDoctor,
        selectedDate,
    );
    const rowsToDisplay = selectedDoctor ? mergedSchedule : appointments;

    useEffect(() => {
        const totalAppointments = selectedDoctor ? rowsToDisplay.filter(a => a.availability === "reservado").length : rowsToDisplay.length;
        const confirmedAppointments = rowsToDisplay.filter(a => a.status === 3).length;
        const availableSlots = doctorSchedule.length;

        setCounter({
            appointments: totalAppointments,
            confirm: confirmedAppointments,
            available: availableSlots,
        });
    }, [appointments, doctorSchedule]);

    const notify = (message, type = "success") => {
        toast(message, {
            style: {
                background: type === "success" ? "#4caf50" : "#f44336",
                color: "#fff",
                padding: "16px",
                borderRadius: "8px",
            },
            duration: 4000,
        });
    };

    const handleRowClick = (appt) => {
        setSelectedAppointment(appt);
        editDialogRef.current?.showModal();
    };

    return (
        <div className="bg-gray-100 dark:bg-gray-800 text-black dark:text-white h-screen">
            <Sidebar
                currentScreen={"Agendamiento"}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                sidebarOpenDesktop={sidebarOpenDesktop}
            />
            <div
                className={`flex flex-col h-screen flex-1 ${
                    sidebarOpenDesktop ? "md:pl-64" : ""
                } transition-all`}
            >
                <LocalHeader
                    dialogRef={dialogRef}
                    buttonText={"Nuevo agendamiento"}
                    buttonAction={() => dialogRef.current?.showModal()}
                    link={"/schedule/agenda"}
                    linkText={"Configuración"}
                    setSelectedDate={setSelectedDate}
                    selectedDate={selectedDate}
                    setSelectedDoctor={setSelectedDoctor}
                    selectedDoctor={selectedDoctor}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    setSearchTerm={setSearchTerm}
                    sidebarOpenDesktop={sidebarOpenDesktop}
                    setSidebarOpenDesktop={setSidebarOpenDesktop}
                    searchAllDates={searchAllDates}
                    setSearchAllDates={setSearchAllDates}
                />
                <main className="flex-1 space-y-6 bg-gray-100 dark:bg-gray-800 py-8 px-6 lg:px-8">
                    {selectedDoctor && (
                        <button
                            onClick={() => setSelectedDoctor(null)}
                            className="px-4 py-2 rounded-full text-sm font-semibold flex justify-between items-center gap-1 text-blue-800 bg-blue-100"
                        >
                            {" "}
                            <span>{selectedDoctor.name}</span>
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    )}
                    <h2 className="text-4xl font-bold dark:text-gray-200">
                        Agendamientos
                    </h2>
                    <div className="flex flex-col justify-start md:flex-row gap-3">
                        <div className="w-72 flex ">
                            <DayCalendar
                                selectedDate={selectedDate}
                                setSelectedDate={setSelectedDate}
                            />
                        </div>
                        <Card
                            icon={<CalendarIcon className="h-6 w-6" />}
                            iconBg={"bg-[#B2DDFF]"}
                            small={"Total de agendamientos"}
                            total={counter.appointments}
                        />
                        <Card
                            icon={<CheckIcon className="h-6 w-6" />}
                            iconBg={"bg-[#A6F4C5]"}
                            small={"Confirmados"}
                            total={counter.confirm}
                        />
                        {selectedDoctor && (
                            <Card
                                icon={<ClockIcon className="h-6 w-6" />}
                                iconBg={"bg-[#D9D6FE]"}
                                small={"Turnos disponibles"}
                                total={counter.available}
                            />
                        )}
                    </div>
                    <div className="flex flex-col md:flex-row gap-5">
                        <div className="w-full max-h-[660px] overflow-auto rounded relative">
                            {selectedDoctor ? (
                                <DrAppointmentTable
                                    data={rowsToDisplay}
                                    onRowClick={handleRowClick}
                                    loading={loadingAppointments || loadingSchedule}
                                    onSave={() => {
                                        fetchAppointments();
                                        fetchSchedule();
                                    }}
                                    notify={notify}
                                />
                            ) : (
                                <AppointmentTable
                                    data={rowsToDisplay}
                                    onRowClick={handleRowClick}
                                    loading={loadingAppointments}
                                    onSave={() => {
                                        fetchAppointments();
                                        fetchSchedule();
                                    }}
                                    notify={notify}
                                />
                            )}
                        </div>
                    </div>
                    <DialogMenu dialogRef={dialogRef}>
                        <AppointmentForm
                            dialogRef={dialogRef}
                            onSave={() => {
                                fetchAppointments();
                                fetchSchedule();
                            }}
                            selectedDoctor={selectedDoctor}
                            notify={notify}
                        />
                    </DialogMenu>
                    <DialogMenu dialogRef={editDialogRef}>
                        <AppointmentForm
                            dialogRef={editDialogRef}
                            initialAppointment={selectedAppointment}
                            onSave={() => {
                                fetchAppointments();
                                fetchSchedule();
                            }}
                            notify={notify}
                        />
                    </DialogMenu>
                </main>
            </div>
            <Toaster />
        </div>
    );
}

// const ClosestAvailability = ({ dates }) => {
//     return (
//         <div className="p-3 text-gray-700 dark:text-gray-200">
//             <h3 className="text-center border-b dark:border-gray-700 p-3 text-gray-900 dark:text-white font-bold text-lg">
//                 Disponibilidad más cercana
//             </h3>
//             <div className="flex justify-between items-center border-b dark:border-gray-700 p-3">
//                 <span>Doctor</span>
//                 <span>Fecha</span>
//             </div>
//             <ul>
//                 {dates.map((date) => (
//                     <li
//                         key={date.id}
//                         className="flex justify-between items-center border-b dark:border-gray-700 px-3 py-2"
//                     >
//                         <span>{date.doctor}</span>
//                         <span>{date.date}</span>
//                     </li>
//                 ))}
//             </ul>
//         </div>
//     );
// };

const Card = ({ icon, iconBg, small, total }) => {
    return (
        <div className="flex flex-col gap-2 max-h-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 w-72">
            <div
                className={`${iconBg} rounded-lg w-10 h-10 flex items-center justify-center text-white`}
            >
                {icon}
            </div>
            <small className="text-gray-600 font-semibold">{small}</small>
            <span className="text-4xl font-bold">{total}</span>
        </div>
    );
};

const LocalHeader = ({
    dialogRef,
    selectedDate,
    setSelectedDate,
    setSelectedDoctor,
    selectedDoctor,
    setSidebarOpen,
    setSearchTerm,
    sidebarOpen,
    setSidebarOpenDesktop,
    sidebarOpenDesktop,
    searchAllDates,
    setSearchAllDates,
}) => {
    const [doctors, setDoctors] = useState([]);

    const fromInputDate = (value) => {
        if (!value) return null;
        const [y, m, d] = value.split("-").map(Number);
        return new Date(y, m - 1, d);
    };

    const handleChange = (field, value) => {;
        setSelectedDoctor(value);
    };

    useEffect(() => {
        api.doctors.getMulti().then((response) => {
            const data = response;
            const doctorsOptions = data.map((doc) => ({
                ...doc,
                name: `${doc.firstName} ${doc.lastName} ${doc.govId}`,
            }));
            setDoctors(doctorsOptions);
        });
    }, []);
    return (
        <div className="sticky top-0 z-10 flex justify-between h-16 bg-white dark:bg-gray-900 shadow items-center px-5 md:px-10 md:gap-40 gap-1 py-5">
            <div className="flex flex-1 gap-3 w-5/12">
                <button
                    className="h-11 w-11 border rounded-lg border-gray-200 dark:border-gray-700 flex justify-center items-center"
                    onClick={() => {
                        setSidebarOpen(!sidebarOpen);
                        setSidebarOpenDesktop(!sidebarOpenDesktop);
                    }}
                >
                    <Bars3CenterLeftIcon className="h-6 w-6" />
                </button>
                <div className=" flex items-center text-gray-700 bg-gray-100 border border-gray-300 rounded-lg px-2 w-full input-base">
                    <label htmlFor="search">
                        <MagnifyingGlassIcon className="h-6 w-6" />
                    </label>
                    <input
                        type="search"
                        name="search"
                        id="search"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border-none w-full focus:outline-none focus:ring-0 focus:border-transparent bg-transparent "
                        placeholder="Buscar"
                    />
                </div>
            </div>
            <div className=" hidden md:flex justify-end gap-3">
                <div className="flex gap-2">
                    <div className="elative inline-block text-left">
                        <label className="flex gap-2 items-center py-4 px-5 text-sm">
                            <input
                                type="checkbox"
                                checked={searchAllDates}
                                onChange={(e) =>
                                    setSearchAllDates(e.target.checked)
                                }
                            />
                            Buscar en todas las fechas
                        </label>
                    </div>
                    <div className="flex items-center">
                        <Menu as="div" className="relative inline text-left">
                            <div>
                                <Menu.Button className="inline-flex items-center w-full justify-center gap-x-1.5 rounded-md bg-white dark:bg-gray-900 px-3 py-2 text-sm text-black dark:text-white ring-0 outline-0">
                                    Médico
                                    <ChevronDownIcon
                                        className="-mr-1 h-5 w-5 text-black dark:text-white"
                                        aria-hidden="true"
                                    />
                                </Menu.Button>
                            </div>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="absolute z-10 mt-2 w-64 origin-top-right rounded-md bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div className="py-4 flex flex-col gap-3 px-5">
                                        <SimpleCombobox
                                            inputClassName="relative mt-1"
                                            placeholder={"Buscar"}
                                            handleChange={handleChange}
                                            name="doctor"
                                            label="Doctor"
                                            labelClassname="font-semibold"
                                            value={selectedDoctor}
                                            options={doctors}
                                        />
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Link
                        to="/schedule/agenda"
                        className="px-3 py-2 rounded-lg flex justify-center items-center gap-2 border  dark:border-gray-700"
                    >
                        <Cog6ToothIcon className="h-6 w-6" />{" "}
                        <span>Configurar agenda</span>
                    </Link>
                    <button
                        className="px-3 py-2 rounded-lg flex justify-center items-center gap-2 text-white bg-[#465FFF]"
                        onClick={() => dialogRef.current?.showModal()}
                    >
                        <PlusIcon className="h-6 w-6" />{" "}
                        <span>Nuevo Agendamiento</span>
                    </button>
                </div>
            </div>
            <div className="sm:hidden">
                <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700">
                        <AdjustmentsHorizontalIcon className="h-6 w-6" />
                    </Menu.Button>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right rounded-md bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-4 flex flex-col gap-4">
                            {/* Fecha */}
                            <div>
                                <label
                                    htmlFor="date"
                                    className="text-sm font-medium"
                                >
                                    Fecha
                                </label>
                                <input
                                    type="date"
                                    name="date"
                                    id="date"
                                    value={
                                        selectedDate
                                            ? selectedDate
                                                  .toISOString()
                                                  .split("T")[0]
                                            : ""
                                    }
                                    onChange={(e) =>
                                        setSelectedDate(
                                            fromInputDate(e.target.value)
                                        )
                                    }
                                    className="input-base mt-1 w-full"
                                />
                            </div>

                            {/* Doctor */}
                            <div>
                                <label className="text-sm font-medium">
                                    Médico
                                </label>
                                <SimpleCombobox
                                    inputClassName="relative mt-1 w-full"
                                    placeholder={"Buscar"}
                                    handleChange={handleChange}
                                    name="doctor"
                                    label="Doctor"
                                    value={selectedDoctor}
                                    options={doctors}
                                />
                            </div>

                            {/* Configuración */}
                            <Link
                                to="/schedule/agenda"
                                className="px-3 py-2 rounded-lg flex justify-center items-center gap-2 border dark:border-gray-700"
                            >
                                <Cog6ToothIcon className="h-6 w-6" />
                                <span>Configurar agenda</span>
                            </Link>

                            {/* Nuevo agendamiento */}
                            <button
                                className="px-3 py-2 rounded-lg flex justify-center items-center gap-2 text-white bg-[#465FFF]"
                                onClick={() => dialogRef.current?.showModal()}
                            >
                                <PlusIcon className="h-6 w-6" />
                                <span>Nuevo Agendamiento</span>
                            </button>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>
        </div>
    );
};
